import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "@/hooks/use-translations";
import { api, isAgentBusy, isDeletionBlocked, isStaleAnswer, type ChatHistoryMessage } from "@/lib/api";

interface UseCanvasChatOptions {
	projectId: string | undefined;
	chatMessages: ChatHistoryMessage[];
	/** Clears the agent's prompt and returns the id of the question the message answers. */
	clearQuestion: () => string | undefined;
	/** Reconnects to the agent's questions; the server re-sends the one still waiting. */
	resyncQuestion: () => void;
}

/** Canvas chat: composer state, send mutation (optimistic) and auto-scroll. */
export function useCanvasChat({
	projectId,
	chatMessages,
	clearQuestion,
	resyncQuestion,
}: UseCanvasChatOptions) {
	const t = useTranslations("workspace");
	const queryClient = useQueryClient();
	const chatEndRef = useRef<HTMLDivElement>(null);

	// --- Chat State ---
	const [chatInput, setChatInput] = useState("");

	// --- Scroll chat on new message ---
	useEffect(() => {
		chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [chatMessages]);

	// --- Chat handlers ---
	const chatMutation = useMutation({
		mutationFn: async ({ question, questionId }: { question: string; questionId?: string }) => {
			const result = await api.sendChatMessage(projectId!, question, undefined, undefined, undefined, undefined, questionId);
			// Simulate slight thinking delay required for UX
			await new Promise((resolve) => setTimeout(resolve, 600));
			return result;
		},
		onMutate: async ({ question: newQuestion }) => {
			await queryClient.cancelQueries({ queryKey: ["chatHistory", projectId] });
			const previousMessages = queryClient.getQueryData<ChatHistoryMessage[]>([
				"chatHistory",
				projectId,
			]);

			const optimisticMessage = {
				id: `temp-${Date.now()}`,
				role: "user",
				content: newQuestion,
				createdAt: new Date().toISOString(),
			} as any;

			queryClient.setQueryData<ChatHistoryMessage[]>(
				["chatHistory", projectId],
				(old) => {
					return [...(old || []), optimisticMessage];
				},
			);

			// Scroll to bottom after state update
			setTimeout(() => {
				chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
			}, 50);

			return { previousMessages };
		},
		onError: (err, sent, context) => {
			// An agent mid-run refuses the message — expected, not a failure. Text in the interface language, not the server's.
			if (isAgentBusy(err)) {
				toast.info(t("agentBusy"));
			} else if (isDeletionBlocked(err)) {
				// A deletion waits for its buttons: the message is kept for later.
				toast.info(t("deletion.blocked"));
				setChatInput((current) => current || sent.question);
			} else if (isStaleAnswer(err)) {
				// The answer was for a question that is gone or unknown to this page: show the current one.
				toast.info(t("questionChanged"));
				setChatInput((current) => current || sent.question);
				resyncQuestion();
			} else {
				toast.error(
					err instanceof Error ? err.message : "Failed to send message",
				);
			}

			if (context?.previousMessages) {
				queryClient.setQueryData(
					["chatHistory", projectId],
					context.previousMessages,
				);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["chatHistory", projectId] });
		},
	});

	const handleAgentOptionSelect = (label: string) => {
		if (!projectId || chatMutation.isPending) return;
		const questionId = clearQuestion();
		chatMutation.mutate({ question: label, questionId });
	};

	const handleSendMessage = (e: React.FormEvent) => {
		e.preventDefault();
		if (!chatInput.trim() || !projectId || chatMutation.isPending) return;

		// While the agent is waiting, this message becomes its answer
		const questionId = clearQuestion();
		chatMutation.mutate({ question: chatInput.trim(), questionId });
		setChatInput("");
	};

	return {
		chatInput,
		setChatInput,
		chatEndRef,
		isSending: chatMutation.isPending,
		handleAgentOptionSelect,
		handleSendMessage,
	};
}
